import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useParsedProducts } from '@lib/core/products/hooks/useParsedProducts';
import { useApp } from '@lib/core/service/hooks';
import { getMultipleUniqueRandomItemsFromArray, shuffleArray } from '@lib/core/service/utils';
import {
  selectCurrentDiscoveryQuizProductInstance,
  selectDiscoveryQuizProductInstances,
  selectIsDiscoveryQuizProductInstancesLoading,
} from '@lib/tools/discoveryQuiz/selectors';
import { TDiscoveryQuiz, TDiscoveryQuizData } from '@lib/tools/discoveryQuiz/types';
import { localeCommon } from '@lib/tools/locale/source/web/common';
import {
  DISCOVERY_QUIZ_RANDOM_PRODUCTS_COUNT,
  DISCOVER_QUIZ_AROMAS_ATTRIBUTE,
  DISCOVER_QUIZ_TASTE_ATTRIBUTE,
} from '@lib/tools/shared/helpers/consts';

export const useDiscoveryQuiz = () => {
  const { aromasQuizType, tasteQuizType, aromasQuizTitle, tasteQuizTitle, aromasQuizBtn, tasteQuizFinishBtn } =
    localeCommon.discoveryQuizModal;

  const { locale } = useApp();
  const { parseProduct } = useParsedProducts();

  const discoveryQuizProductInstances = useSelector(selectDiscoveryQuizProductInstances);
  const currentDiscoveryQuizProductInstance = useSelector(selectCurrentDiscoveryQuizProductInstance);
  const isDiscoveryQuizProductInstancesLoading = useSelector(selectIsDiscoveryQuizProductInstancesLoading);

  const randomProducts = useMemo(() => {
    if (!discoveryQuizProductInstances || !currentDiscoveryQuizProductInstance) return [];
    return getMultipleUniqueRandomItemsFromArray(
      discoveryQuizProductInstances.filter(product => {
        const aromaProducts = product?.product?.attributes?.[`${DISCOVER_QUIZ_AROMAS_ATTRIBUTE}_${locale}`];
        const tasteProducts = product?.product?.attributes?.[`${DISCOVER_QUIZ_TASTE_ATTRIBUTE}_${locale}`];

        return (
          product.identifier !== currentDiscoveryQuizProductInstance.identifier && !!aromaProducts && !!tasteProducts
        );
      }),
      DISCOVERY_QUIZ_RANDOM_PRODUCTS_COUNT,
    );
  }, [discoveryQuizProductInstances, currentDiscoveryQuizProductInstance, locale]);

  const createQuizData = ({ quizAttribute, btnText, quizTitle, quizType, randomProduct }): TDiscoveryQuizData => {
    const getAttribute = (product, attributeKey) => product?.product?.attributes[`${attributeKey}_${locale}`];

    const correctText = getAttribute(currentDiscoveryQuizProductInstance, quizAttribute);
    const incorrectText = getAttribute(randomProduct, quizAttribute);

    const answers = shuffleArray([
      { id: 1, isCorrectAnswer: true, text: correctText },
      { id: 2, isCorrectAnswer: false, text: incorrectText },
    ]);

    return {
      answers,
      btnText,
      quizTitle,
      quizType,
    };
  };

  const discoveryQuizData = useMemo(
    () => [
      createQuizData({
        btnText: aromasQuizBtn,
        quizAttribute: DISCOVER_QUIZ_AROMAS_ATTRIBUTE,
        quizTitle: aromasQuizTitle,
        quizType: aromasQuizType,
        randomProduct: randomProducts[0],
      }),
      createQuizData({
        btnText: tasteQuizFinishBtn,
        quizAttribute: DISCOVER_QUIZ_TASTE_ATTRIBUTE,
        quizTitle: tasteQuizTitle,
        quizType: tasteQuizType,
        randomProduct: randomProducts[1],
      }),
    ],
    [currentDiscoveryQuizProductInstance, randomProducts],
  );

  const discoveryQuiz: TDiscoveryQuiz = {
    currentDiscoveryQuizProductInstance: parseProduct(currentDiscoveryQuizProductInstance),
    discoveryQuizData,
  };

  return { discoveryQuiz, isDiscoveryQuizProductInstancesLoading };
};
